/* eslint-disable react/no-array-index-key */
/* eslint-disable no-param-reassign */
// @ts-nocheck
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
  Box,
  IconButton,
  alpha,
} from '@mui/material';

import { cloneDeep } from 'lodash';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MODULE_PERMISSION_DESCRIPTION } from '../../const/CommonConst';

const permissionArray = ['View', 'Manage', 'None', 'Custom'];
const SPACE_EVENLY = 'space-evenly';
const UNAUTHORIZED_MESSAGE = 'You are not authorized to access this!';

const RenderModulePermissions = ({
  value,
  getHandleOnChangeFunction,
  mode,
  project,
  activeUser,
  ownerModulesSubmodulesList,
}) => {
  const isPermissionDisabled = () => {
    return (
      mode === 'EDIT' &&
      project?.is_collaborative &&
      project?.user_id !== activeUser?.id
    );
  };
  const isPermissionDisabledForTitle = () => {
    return project?.is_collaborative && project?.user_id !== activeUser?.id;
  };
  const getDisabledCondition = useCallback((moduleDetails, permissionType) => {
    const ownerPermissionValue =
      ownerModulesSubmodulesList[moduleDetails.objKey]?.permissions[
        moduleDetails?.key
      ];
    const disabledCondition =
      isPermissionDisabled() || ownerPermissionValue === 'None';
    switch (permissionType) {
      case 'Manage':
      case 'Custom':
        return disabledCondition || ownerPermissionValue === 'View';
      case 'View':
        return disabledCondition;
      case 'None':
        break;
      default:
        return null;
    }
  }, []);
  const getTitleForRadioGroup = useCallback((moduleDetails, permissionType) => {
    const ownerPermissionValue =
      ownerModulesSubmodulesList[moduleDetails.objKey]?.permissions[
        moduleDetails?.key
      ];
    const disabledCondition =
      isPermissionDisabledForTitle() || ownerPermissionValue === 'None';
    const manageCondition =
      disabledCondition || ownerPermissionValue === 'View';
    switch (permissionType) {
      case 'Manage':
      case 'Custom':
        return manageCondition ? UNAUTHORIZED_MESSAGE : '';
      case 'View':
        return disabledCondition ? UNAUTHORIZED_MESSAGE : '';
      case 'None':
        break;
      default:
        return null;
    }
  }, []);
  return (
    <Stack direction="row">
      <FormControl>
        <RadioGroup
          row
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: SPACE_EVENLY,
            marginLeft: 2,
          }}
          aria-label="Permissions"
          name={`permissions-${value?.objKey}`}
        >
          {['View', 'Manage', 'None'].map((perm) => (
            <Box key={perm}>
              <FormControlLabel
                control={
                  <Radio
                    value={perm}
                    checked={value?.permissions[value?.key] === perm}
                    inputProps={{ title: perm }}
                    onChange={() => getHandleOnChangeFunction(perm)}
                    disabled={getDisabledCondition(value, perm)}
                    title={getTitleForRadioGroup(value, perm)}
                    sx={{ cursor: 'pointer' }}
                  />
                }
                aria-labelledby={`${perm}-${value?.objKey}`}
                aria-checked={value?.permissions[value?.key] === perm}
              />
            </Box>
          ))}
          {/* Visually hidden live region for announcements */}
          <div
            aria-live="polite"
            aria-atomic="true"
            role="status"
            style={{
              position: 'absolute',
              clip: 'rect(0 0 0 0)', // Clips the element completely so it’s not visible.
              overflow: 'hidden',
            }}
          >
            {(() => {
              const permissionMessages = {
                Manage: 'Manage permission allowed.',
                View: 'View permission allowed.',
                None: 'No permission allowed.',
              };
              return permissionMessages[value?.permissions[value?.key]] || '';
            })()}
          </div>

          {Object.values(value?.subModules).length > 1 ? (
            <Box>
              <FormControlLabel
                control={
                  <Radio
                    checked={value?.permissions[value?.key] === 'Custom'}
                    inputProps={{ title: 'Custom' }}
                    disabled={getDisabledCondition(value, 'Custom')}
                    title={getTitleForRadioGroup(value, 'Custom')}
                  />
                }
              />
            </Box>
          ) : (
            <Box>
              <FormControlLabel
                control={
                  <Radio
                    checked={false}
                    disabled
                    title="You are not authorized to access this!"
                  />
                }
              />
            </Box>
          )}
        </RadioGroup>
      </FormControl>
    </Stack>
  );
};

RenderModulePermissions.propTypes = {
  value: PropTypes.shape({
    objKey: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    permissions: PropTypes.objectOf(PropTypes.string).isRequired,
    subModules: PropTypes.objectOf(PropTypes.shape({})).isRequired,
  }).isRequired,
  getHandleOnChangeFunction: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  project: PropTypes.shape({
    is_collaborative: PropTypes.bool,
    user_id: PropTypes.string,
  }).isRequired,
  activeUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  ownerModulesSubmodulesList: PropTypes.objectOf(
    PropTypes.shape({
      permissions: PropTypes.objectOf(PropTypes.string).isRequired,
    })
  ).isRequired,
};

const RenderGranularpermissions = ({
  handlePermissionChangeSubModule,
  value,
  subModule,
  permission,
  ownerPermission,
  operationName,
  conditionToRenderGranularPermissionModules,
  mode,
  project,
  activeUser,
}) => {
  const handleViewChangeSubModule = useCallback(() => {
    handlePermissionChangeSubModule(value?.objKey, subModule?.objKey, 'View');
  }, [handlePermissionChangeSubModule, value, subModule]);

  const handleManageChangeSubModule = useCallback(() => {
    handlePermissionChangeSubModule(value?.objKey, subModule?.objKey, 'Manage');
  }, [handlePermissionChangeSubModule, value, subModule]);

  const handleNoneChangeSubModule = useCallback(() => {
    handlePermissionChangeSubModule(value?.objKey, subModule?.objKey, 'None');
  }, [handlePermissionChangeSubModule, value, subModule]);

  const handleYesChangeSubModule = useCallback(() => {
    handlePermissionChangeSubModule(value?.objKey, subModule?.objKey, 'Yes');
  }, [handlePermissionChangeSubModule, value, subModule]);

  const handleNoChangeSubModule = useCallback(() => {
    handlePermissionChangeSubModule(value?.objKey, subModule?.objKey, 'No');
  }, [handlePermissionChangeSubModule, value?.objKey, subModule?.objKey]);

  const disableGranularAndBinaryPermission = useCallback((permissionType) => {
    const primaryCondition =
      mode === 'EDIT' &&
      project?.is_collaborative &&
      project?.user_id !== activeUser?.id;
    const viewCondition = primaryCondition || ownerPermission === 'None';
    const manageCondition = viewCondition || ownerPermission === 'View';
    const yesCondition = primaryCondition || ownerPermission === 'No';
    switch (permissionType) {
      case 'Manage':
        return manageCondition;
      case 'View':
        return viewCondition;
      case 'None':
        return primaryCondition;
      case 'Yes':
        return yesCondition;
      case 'No':
        return primaryCondition;
      default:
        return null;
    }
  }, []);

  const titleForGranularAndBinaryPermission = useCallback((permissionType) => {
    const primaryCondition =
      project?.is_collaborative && project?.user_id !== activeUser?.id;
    const viewCondition = primaryCondition || ownerPermission === 'None';
    const manageCondition = viewCondition || ownerPermission === 'View';
    const yesCondition = primaryCondition || ownerPermission === 'No';
    switch (permissionType) {
      case 'Manage':
        return manageCondition ? UNAUTHORIZED_MESSAGE : '';
      case 'View':
        return viewCondition ? UNAUTHORIZED_MESSAGE : '';
      case 'None':
        return primaryCondition ? UNAUTHORIZED_MESSAGE : '';
      case 'Yes':
        return yesCondition ? UNAUTHORIZED_MESSAGE : '';
      case 'No':
        return primaryCondition ? UNAUTHORIZED_MESSAGE : '';
      default:
        return '';
    }
  }, []);
  return (
    <Box mb={1} mt={1} display="flex">
      <Grid xs={12} display="flex">
        {subModule?.label !== 'Create project' &&
          subModule?.label !== 'Is collaborative' && (
            <Grid item xs={4}>
              <Typography
                pr={2}
                pl={3}
                title={
                  MODULE_PERMISSION_DESCRIPTION[
                    `${value?.key}_${subModule?.key}`
                  ]
                }
                tabIndex={0}
              >
                {subModule?.label} :
              </Typography>
            </Grid>
          )}
        <Grid item xs={8}>
          {conditionToRenderGranularPermissionModules(value) ? (
            <Box>
              <Stack direction="row">
                <FormControl>
                  <RadioGroup
                    row
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: SPACE_EVENLY,
                      margin: 0,
                    }}
                  >
                    <Box>
                      <FormControlLabel
                        control={
                          <Radio
                            value="View"
                            checked={permission === 'View'}
                            inputProps={{
                              title: 'view',
                            }}
                            onChange={handleViewChangeSubModule}
                            disabled={disableGranularAndBinaryPermission(
                              'View'
                            )}
                            sx={{
                              cursor: 'pointer',
                            }}
                          />
                        }
                        title={titleForGranularAndBinaryPermission('View')}
                        label="View"
                      />
                    </Box>
                    <Box>
                      <FormControlLabel
                        control={
                          <Radio
                            value="Manage"
                            checked={permission === 'Manage'}
                            inputProps={{
                              title: 'Manage',
                            }}
                            disabled={disableGranularAndBinaryPermission(
                              'Manage'
                            )}
                            onChange={handleManageChangeSubModule}
                            sx={{
                              cursor: 'pointer',
                            }}
                          />
                        }
                        title={titleForGranularAndBinaryPermission('Manage')}
                        label="Manage"
                      />
                    </Box>
                    <Box>
                      <FormControlLabel
                        control={
                          <Radio
                            value="None"
                            checked={permission === 'None'}
                            inputProps={{
                              title: 'None',
                            }}
                            disabled={disableGranularAndBinaryPermission(
                              'None'
                            )}
                            onChange={handleNoneChangeSubModule}
                            sx={{
                              cursor: 'pointer',
                            }}
                          />
                        }
                        sx={{
                          cursor: 'pointer',
                        }}
                        title={titleForGranularAndBinaryPermission('None')}
                        label="None"
                      />
                    </Box>
                  </RadioGroup>
                </FormControl>
              </Stack>
            </Box>
          ) : (
            operationName !== 'ProjectOverview_CreateProject' &&
            operationName !== 'ProjectOverview_IsCollaborative' && (
              <Stack direction="row">
                <FormControl>
                  <Box width="100%">
                    <RadioGroup
                      row
                      pl={4}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        paddingLeft: 8,
                      }}
                    >
                      <Box mr={2}>
                        <FormControlLabel
                          control={
                            <Radio
                              value="Yes"
                              onChange={handleYesChangeSubModule}
                              inputProps={{
                                title: 'Yes',
                              }}
                              checked={permission === 'Yes'}
                              disabled={disableGranularAndBinaryPermission(
                                'Yes'
                              )}
                              sx={{
                                cursor: 'pointer',
                              }}
                              title={titleForGranularAndBinaryPermission('Yes')}
                            />
                          }
                          label="Yes"
                        />
                      </Box>
                      <Box>
                        <FormControlLabel
                          control={
                            <Radio
                              onChange={handleNoChangeSubModule}
                              inputProps={{
                                title: 'No',
                              }}
                              disabled={disableGranularAndBinaryPermission(
                                'No'
                              )}
                              checked={permission === 'No'}
                              sx={{
                                cursor: 'pointer',
                              }}
                              title={titleForGranularAndBinaryPermission('No')}
                            />
                          }
                          label="No"
                          value="No"
                        />
                      </Box>
                    </RadioGroup>
                  </Box>
                </FormControl>
              </Stack>
            )
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

RenderGranularpermissions.propTypes = {
  handlePermissionChangeSubModule: PropTypes.func.isRequired,
  value: PropTypes.shape({
    objKey: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    permissions: PropTypes.objectOf(PropTypes.string).isRequired,
    subModules: PropTypes.objectOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string,
      })
    ),
  }).isRequired,
  subModule: PropTypes.shape({
    objKey: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
  }).isRequired,
  permission: PropTypes.string.isRequired,
  ownerPermission: PropTypes.string.isRequired,
  operationName: PropTypes.string.isRequired,
  conditionToRenderGranularPermissionModules: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  project: PropTypes.shape({
    is_collaborative: PropTypes.bool,
    user_id: PropTypes.string,
  }).isRequired,
  activeUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
};

const RenderModule = ({
  value,
  modulekey,
  handlePermissionChange,
  expanded,
  handleChange,
  mode,
  project,
  activeUser,
  ownerModulesSubmodulesList,
  conditionToRenderBinaryPermissionModules,
  conditionToRenderGranularPermissionModules,
  handlePermissionChangeSubModule,
}) => {
  const getHandleOnChangeFunction = (permissionType) => {
    switch (permissionType) {
      case 'Manage':
        return handlePermissionChange(value?.objKey, 'Manage');
      case 'View':
        return handlePermissionChange(value?.objKey, 'View');
      case 'None':
        return handlePermissionChange(value?.objKey, 'None');
      default:
        return null;
    }
  };
  if (value?.objKey === 'ST') return null;
  return (
    <>
      <Box width="100%">
        <Accordion
          expanded={expanded === `panel${modulekey}`}
          aria-expanded={expanded === `panel${modulekey}`}
          className="permission-accordion"
        >
          <AccordionSummary
            aria-controls={`panel${modulekey}-content`}
            id={`panel${modulekey}-header`}
            expandIcon={
              <IconButton
                aria-label="Expand or collapse"
                aria-hidden="true"
                onClick={() => {
                  handleChange(`panel${modulekey}`);
                }}
                sx={{
                  margin: '0px',
                  padding: 0,
                }}
              >
                <ExpandMoreIcon aria-hidden="true" alt="" />
              </IconButton>
            }
          >
            <Grid xs={12} display="flex">
              <Grid item xs={4} pt={0.8}>
                <Typography pr={2} tabIndex={0}>
                  {value?.label}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <RenderModulePermissions
                  value={value}
                  getHandleOnChangeFunction={getHandleOnChangeFunction}
                  mode={mode}
                  project={project}
                  activeUser={activeUser}
                  ownerModulesSubmodulesList={ownerModulesSubmodulesList}
                />
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              paddingRight: '0',
            }}
          >
            <Box>
              {conditionToRenderBinaryPermissionModules(value) ? (
                <Box
                  sx={{
                    backgroundColor: (theme) =>
                      alpha(theme?.palette?.secondary?.light, 0.4),
                    color: (theme) => theme?.palette?.secondary?.contrastText,
                  }}
                  py={0.5}
                >
                  <Grid container xs={12}>
                    <Grid item xs={4}>
                      <Typography variant="body1" ml={3}>
                        Action
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body1" ml={8.5}>
                        Access
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box
                  sx={{
                    backgroundColor: (theme) =>
                      alpha(theme?.palette?.secondary?.light, 0.4),
                  }}
                  p={0.5}
                >
                  <Typography variant="body1" pl={3}>
                    Sub Module
                  </Typography>
                </Box>
              )}
              {value?.subModules &&
                Object.values(value?.subModules)?.map((subModule) => {
                  const permission =
                    value.permissions[`${value?.key}_${subModule?.key}`];
                  const moduleObjKey =
                    ownerModulesSubmodulesList[value?.objKey];
                  const ownerPermission =
                    moduleObjKey.permissions[`${value?.key}_${subModule?.key}`];
                  const operationName = `${value?.key}_${subModule?.key}`;
                  return (
                    <RenderGranularpermissions
                      value={value}
                      key={subModule?.key}
                      subModule={subModule}
                      permission={permission}
                      ownerPermission={ownerPermission}
                      operationName={operationName}
                      conditionToRenderGranularPermissionModules={
                        conditionToRenderGranularPermissionModules
                      }
                      handlePermissionChangeSubModule={
                        handlePermissionChangeSubModule
                      }
                    />
                  );
                })}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Divider />
    </>
  );
};

RenderModule.propTypes = {
  value: PropTypes.shape({
    objKey: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    permissions: PropTypes.objectOf(PropTypes.string).isRequired,
    subModules: PropTypes.objectOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string,
      })
    ),
  }).isRequired,
  modulekey: PropTypes.string.isRequired,
  handlePermissionChange: PropTypes.func.isRequired,
  expanded: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  project: PropTypes.shape({
    is_collaborative: PropTypes.bool,
    user_id: PropTypes.string,
  }).isRequired,
  activeUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  ownerModulesSubmodulesList: PropTypes.objectOf(
    PropTypes.shape({
      permissions: PropTypes.objectOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  conditionToRenderBinaryPermissionModules: PropTypes.func.isRequired,
  conditionToRenderGranularPermissionModules: PropTypes.func.isRequired,
  handlePermissionChangeSubModule: PropTypes.func.isRequired,
};

function UserPermission({
  project,
  mode,
  activeUser,
  moduleSubModules,
  selectedTab,
  setModuleSubModules,
  ownerModulesSubmodulesList,
}) {
  const udfViewOnlyPermission = [
    'UDF_ViewDetails',
    'UDF_VersionHistory',
    'UDF_CompareJobs',
    'UDF_ViewJobQueue',
    'UDF_ViewJobResults',
    'UDF_ViewUDFList',
  ];
  const pipelineViewOnlyPermission = [
    'Pipeline_ViewExecutionHistory',
    'Pipeline_ViewExecutionResult',
    'Pipeline_EditPipeline',
    'Pipeline_ListPipeline',
    'Pipeline_ListPublishedPipeline',
  ];
  const docViewOnlyPermission = ['AutomatedDoc_EditTemplate'];
  const [expanded, setExpanded] = useState('panel1');

  /**
   * @function handleChange
   * @description function to handle the change of the accordion
   * @param {string} pannel - pannel name of the accordion
   * @returns {void}
   */
  const handleChange = (pannel) => {
    setExpanded(expanded === pannel ? '' : pannel);
  };

  const isMainModuleItem = (item) => {
    return [
      'AutomatedDoc',
      'ProjectOverview',
      'UDF',
      'Pipeline',
      'ModelManager',
      'TaskManager',
    ].includes(item);
  };

  const handleOtherModulePermissions = (
    name,
    item,
    value,
    permissions,
    ownerPermissions
  ) => {
    if (
      ownerPermissions[item] === 'None' &&
      (value === 'Manage' || value === 'View')
    ) {
      permissions[item] = 'None';
    } else if (
      ownerPermissions[item] === 'View' &&
      (value === 'Manage' || value === 'View')
    ) {
      permissions[item] = 'View';
    } else {
      permissions[item] = value;
    }
  };

  const isMainModule = (name) => {
    return ['PO', 'UDF', 'AD', 'PIP', 'MM', 'TM'].includes(name);
  };

  const handleMainModulePermissions = (
    name,
    item,
    value,
    permissions,
    ownerPermissions
  ) => {
    if (isMainModuleItem(item)) {
      permissions[item] = value;
    } else if (value === 'Manage') {
      permissions[item] = ownerPermissions[item] === 'No' ? 'No' : 'Yes';
    } else if (
      value === 'View' &&
      (udfViewOnlyPermission.includes(item) ||
        pipelineViewOnlyPermission.includes(item) ||
        docViewOnlyPermission.includes(item))
    ) {
      permissions[item] = 'Yes';
    } else {
      permissions[item] = 'No';
    }
  };

  /**
   * @function handlePermissionChange
   * @param {string} name - name of the module
   * @param {string} value - value of the permission
   * @description handle the permission change
   * @returns {void}
   */
  const handlePermissionChange = (name, value) => {
    const copyOfModuleSubModules = cloneDeep(
      moduleSubModules[`${selectedTab}`]
    );
    const { permissions } = copyOfModuleSubModules[name];

    Object.keys(permissions).forEach((item) => {
      if (isMainModule(name)) {
        handleMainModulePermissions(
          name,
          item,
          value,
          permissions,
          ownerModulesSubmodulesList[name].permissions
        );
      } else {
        handleOtherModulePermissions(
          name,
          item,
          value,
          permissions,
          ownerModulesSubmodulesList[name].permissions
        );
      }
    });

    setModuleSubModules((prevState) => ({
      ...prevState,
      [selectedTab]: cloneDeep(copyOfModuleSubModules),
    }));
  };

  const stringCompare = (a, b) => a.localeCompare(b);

  const areArraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;

    const sortedArr1 = [...arr1].sort(stringCompare);
    const sortedArr2 = [...arr2].sort(stringCompare);

    return sortedArr1.every((ele, index) => ele === sortedArr2[index]);
  };

  const handleSubModulePermissions = (
    modulePermissions,
    moduleKey,
    permissionKeys,
    moduleName
  ) => {
    const currentPermissions = permissionKeys.filter(
      (item) => modulePermissions[item] === 'Yes'
    );

    const updatePermissionBasedOnModule = (
      _currentPermissions,
      viewOnlyPermissions,
      viewType
    ) => {
      modulePermissions[moduleKey] = areArraysEqual(
        _currentPermissions,
        viewOnlyPermissions
      )
        ? viewType
        : 'Custom';
    };

    switch (moduleName) {
      case 'UDF':
        updatePermissionBasedOnModule(
          currentPermissions,
          udfViewOnlyPermission,
          'View'
        );
        break;
      case 'PIP':
        updatePermissionBasedOnModule(
          currentPermissions,
          pipelineViewOnlyPermission,
          'View'
        );
        break;
      case 'AD':
        updatePermissionBasedOnModule(
          currentPermissions,
          docViewOnlyPermission,
          'View'
        );
        break;
      default:
        modulePermissions[moduleKey] = 'Custom';
        break;
    }
  };

  const handleUniformPermissionsSelection = (
    modulePermissions,
    moduleKey,
    value
  ) => {
    const permissionValues = Object.values(modulePermissions).slice(1);

    const hasNoOrYes = permissionValues.some(
      (ele) => ele === 'No' || ele === 'Yes'
    );

    if (hasNoOrYes) {
      modulePermissions[moduleKey] = value === 'No' ? 'None' : 'Manage';
    } else {
      modulePermissions[moduleKey] = value;
    }
  };

  const handleComplexPermissions = (
    modulePermissions,
    permissionKeys,
    moduleKey,
    value,
    moduleName
  ) => {
    const matchedPermissions = permissionKeys.filter(
      (item) => modulePermissions[item] === value
    );
    const totalPermissions = permissionKeys.length;
    const isProjectOverview = moduleKey === 'ProjectOverview';

    if (
      (matchedPermissions.length === totalPermissions - 2 &&
        isProjectOverview) ||
      matchedPermissions.length === totalPermissions - 1
    ) {
      handleUniformPermissionsSelection(modulePermissions, moduleKey, value);
    } else {
      handleSubModulePermissions(
        modulePermissions,
        moduleKey,
        permissionKeys,
        moduleName
      );
    }
  };

  /**
   * @function handlePermissionChangeSubModule
   * @param {string} moduleName - name of the module
   * @param {string} subModuleName - name of the sub module
   * @param {string} value - value of the permission
   */
  const handlePermissionChangeSubModule = (
    moduleName,
    subModuleName,
    value
  ) => {
    const copyOfModuleSubModules = cloneDeep(moduleSubModules[selectedTab]);
    const modulePermissions = copyOfModuleSubModules[moduleName].permissions;
    const moduleKey = copyOfModuleSubModules[moduleName].key;

    const permissionName = `${moduleKey}_${copyOfModuleSubModules[moduleName].subModules[subModuleName].key}`;
    if (modulePermissions[permissionName]) {
      modulePermissions[permissionName] = value;
    }

    const permissionKeys = Object.keys(modulePermissions);

    if (permissionKeys.length > 2) {
      handleComplexPermissions(
        modulePermissions,
        permissionKeys,
        moduleKey,
        value,
        moduleName
      );
    } else {
      modulePermissions[moduleKey] = value;
    }

    setModuleSubModules((prevState) => ({
      ...prevState,
      [selectedTab]: cloneDeep(copyOfModuleSubModules),
    }));
  };

  const conditionToRenderBinaryPermissionModules = useCallback((value) => {
    return (
      value?.objKey === 'PO' ||
      value.objKey === 'UDF' ||
      value?.objKey === 'PIP' ||
      value.objKey === 'AD' ||
      value?.objKey === 'MM' ||
      value?.objKey === 'TM'
    );
  }, []);

  const conditionToRenderGranularPermissionModules = useCallback((value) => {
    return (
      value?.objKey !== 'PO' &&
      value?.objKey !== 'UDF' &&
      value?.objKey !== 'PIP' &&
      value?.objKey !== 'AD' &&
      value?.objKey !== 'MM' &&
      value?.objKey !== 'TM'
    );
  }, []);

  return (
    <Grid
      container
      sx={{
        height: '700px',
        overflowY: 'auto',
      }}
    >
      <Grid
        container
        sx={{
          backgroundColor: (theme) =>
            alpha(theme?.palette?.secondary?.light, 0.4),
        }}
        paddingBottom={0}
        alignItems="center"
        justifyContent="center"
        xs={12}
        textAlign="center"
        px={2}
        py={1}
      >
        <Grid item xs={4} textAlign="left">
          <Typography
            tabIndex={0}
            variant="h6"
            aria-label="Module"
            sx={{
              color: (theme) => theme?.palette?.secondary?.contrastText,
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
              },
            }}
          >
            Module
          </Typography>
        </Grid>
        <Grid
          item
          xs={8}
          sx={{
            display: 'flex',
            flex: 1,
            width: '100%',
          }}
        >
          <Stack
            direction="row"
            sx={{
              display: 'flex',
              justifyContent: SPACE_EVENLY,
              width: '100%',
            }}
          >
            {permissionArray?.map((option) => (
              <Typography
                tabIndex={0}
                variant="h6"
                key={option}
                aria-label={`${option} permission`}
                sx={{
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                  },
                }}
              >
                {option}
              </Typography>
            ))}
          </Stack>
        </Grid>
      </Grid>

      <Grid style={{ overflow: 'auto', minHeight: '25px' }} xs={12}>
        {Object.values(moduleSubModules[Number(selectedTab)])?.length !== 0 &&
          Object.values(moduleSubModules[Number(selectedTab)])?.map(
            (value, i) => (
              <Grid item key={i} xs={12}>
                <RenderModule
                  value={value}
                  modulekey={i}
                  handlePermissionChange={handlePermissionChange}
                  expanded={expanded}
                  handleChange={handleChange}
                  mode={mode}
                  project={project}
                  activeUser={activeUser}
                  ownerModulesSubmodulesList={ownerModulesSubmodulesList}
                  conditionToRenderBinaryPermissionModules={
                    conditionToRenderBinaryPermissionModules
                  }
                  handlePermissionChangeSubModule={
                    handlePermissionChangeSubModule
                  }
                  conditionToRenderGranularPermissionModules={
                    conditionToRenderGranularPermissionModules
                  }
                />
              </Grid>
            )
          )}
      </Grid>
    </Grid>
  );
}

UserPermission.propTypes = {
  project: PropTypes.oneOfType([PropTypes.object]).isRequired,
  mode: PropTypes.string.isRequired,
  activeUser: PropTypes.oneOfType([PropTypes.object]).isRequired,
  moduleSubModules: PropTypes.oneOfType([PropTypes.object]).isRequired,
  selectedTab: PropTypes.number.isRequired,
  setModuleSubModules: PropTypes.func.isRequired,
  ownerModulesSubmodulesList: PropTypes.oneOfType([PropTypes.object])
    .isRequired,
};

export default UserPermission;
